import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Search, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { StoreHeader } from '../components/StoreHeader';
import { StoreFooter } from '../components/StoreFooter';
import { orderApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const paymentStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const res = await orderApi.getAll();
      setOrders(res.data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const res = await orderApi.getById(searchQuery);
      setOrders([res.data]);
    } catch (error) {
      toast.error('Order not found');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-stone-900">My Orders</h1>
            <p className="text-stone-500 mt-1">Track and manage your orders</p>
          </div>

          {/* Order Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              type="text"
              placeholder="Enter order number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
              data-testid="order-search-input"
            />
            <Button type="submit" variant="outline" data-testid="order-search-btn">
              <Search className="h-4 w-4" />
            </Button>
          </form>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white border border-stone-200 p-12 text-center">
            <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-900 mb-2">No orders found</h2>
            <p className="text-stone-500 mb-6">
              {searchQuery
                ? 'No order matches your search. Try a different order number.'
                : "You haven't placed any orders yet."}
            </p>
            <Link to="/products">
              <Button className="bg-emerald-900 hover:bg-emerald-800">Start Shopping</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white border border-stone-200 overflow-hidden"
                data-testid={`order-${order.order_number}`}
              >
                {/* Order Header */}
                <div className="bg-stone-50 p-4 border-b border-stone-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <p className="text-sm text-stone-500">Order Number</p>
                      <p className="font-mono font-bold text-emerald-900">{order.order_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Date</p>
                      <p className="font-medium">{formatDate(order.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-stone-500">Total</p>
                      <p className="font-bold text-emerald-900">KES {order.total_amount.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={statusColors[order.order_status]}>
                      {order.order_status.toUpperCase()}
                    </Badge>
                    <Badge className={paymentStatusColors[order.payment_status]}>
                      {order.payment_status.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-4">
                  <div className="space-y-3">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-4 text-sm">
                        <div className="flex-1">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-stone-500">
                            Qty: {item.quantity} × KES {item.unit_price.toLocaleString()}
                          </p>

                          {/* Optional size and color */}
                          {(item.size || item.color) && (
                            <p className="text-stone-400">
                              {item.size && <span>Size: {item.size}</span>}
                              {item.size && item.color && <span className="mx-1">|</span>}
                              {item.color && <span>Color: {item.color}</span>}
                            </p>
                          )}
                        </div>
                        <p className="font-medium">KES {item.total_price.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-stone-200 text-sm">
                    <p className="text-stone-500">
                      <span className="font-medium text-stone-700">Delivery:</span> {order.delivery_address}
                    </p>
                    <p className="text-stone-500 mt-1">
                      <span className="font-medium text-stone-700">Payment:</span>{' '}
                      {order.payment_method === 'mpesa' ? 'M-Pesa' : 'Cash on Delivery'}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  loadOrders();
                }}
                className="mx-auto block"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                View All Orders
              </Button>
            )}
          </div>
        )}
      </main>

      <StoreFooter />
    </div>
  );
}
